import React from "react";
import { connect } from "react-redux";
import { ProbedQuestionModel } from "../../models/ProbedQuestionModel";
import TextAnswerRenderer from "../renderers/v2/TextAnswerRenderer";
import { updateProbedQuestion } from "../../redux/actions";
import { useTheme } from "react-jss";
import useStyles from "./ProbedQuestionsContainer.styles";

interface ProbedQuestionsContainerProps {
    mainVariableId: string;
    rendererProps: any;
    multiline?: boolean;
    maxLength?: number;
    lines?: number;
    disabled?: boolean;
    probedQuestions: ProbedQuestionModel[];
    updateProbedQuestion: (mainVariableId: string, id: string, answer: string) => void;
}

const ProbedQuestionsContainer: React.FC<ProbedQuestionsContainerProps> = ({
    mainVariableId,
    rendererProps,
    multiline,
    maxLength,
    lines,
    disabled,
    probedQuestions,
    updateProbedQuestion,
}) => {
    const theme = useTheme();
    const classes = useStyles({ theme });

    // Get the sorted questions
    const sortedQuestions = [...probedQuestions].sort((a, b) => a.order - b.order);

    // Event handler for probed question answer changes
    const handleProbedAnswerChange = (probedQuestion: ProbedQuestionModel) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { value } = event.target;

        // Only update the probed question in the state
        // Don't set variable mentions - this will be done on navigation
        updateProbedQuestion(mainVariableId, probedQuestion.id, value);
    };

    // Handle answer completion for a probed question
    const handleProbedAnswerBlur = (probedQuestion: ProbedQuestionModel) => () => {
        // We no longer need to do anything on blur
        // Variable mentions will be set during navigation
    };

    // Filter questions to only show:
    // 1. All answered questions
    // 2. The first unanswered question
    // This creates a progressive disclosure where the next question only shows
    // after the current one is answered
    const visibleQuestions = sortedQuestions.reduce((visible, question, index, array) => {
        // Skip questions with empty question text - these are marker questions
        if (!question.questionText || question.questionText.trim() === "") {
            return visible;
        }

        // If this question has an answer, it's visible
        if (question.answer && question.answer.trim() !== "") {
            visible.push(question);
            return visible;
        }

        // If this is the first unanswered question we've found, it's visible
        // We check if all previous questions have answers
        const allPreviousQuestionsAnswered = array
            .slice(0, index)
            .every((q) => q.answer && q.answer.trim() !== "");

        if (allPreviousQuestionsAnswered) {
            visible.push(question);
        }

        return visible;
    }, [] as ProbedQuestionModel[]);

    return (
        <div className={classes.container} aria-live="off">
            {visibleQuestions.map((probedQuestion) => (
                <div key={probedQuestion.id} className={classes.questionItem}>
                    <div id={`${probedQuestion.id}_PROBE_LABEL`} className={classes.questionText}>{probedQuestion.questionText}</div>
                    <TextAnswerRenderer
                        {...rendererProps}
                        multiline={multiline}
                        inputType="text"
                        placeholder=""
                        maxLength={maxLength}
                        onChange={handleProbedAnswerChange(probedQuestion)}
                        onBlur={handleProbedAnswerBlur(probedQuestion)}
                        value={probedQuestion.answer || ""}
                        disabled={disabled}
                        lines={lines}
                        isProbQuestion={true}
                        probQuestionId={probedQuestion.id}
                        ariaLabelledBy={`${probedQuestion.id}_PROBE_LABEL`}
                    />
                </div>
            ))}
        </div>
    );
};

// Map state to props
const mapStateToProps = (state: any, ownProps: any) => {
    // Access the interview slice first, then variables, adding null checks at each level
    const interview = state?.interview || state;
    const variables = interview?.variables || {};
    const probedQuestions = variables?.probedQuestions || {};

    return {
        probedQuestions: probedQuestions[ownProps.mainVariableId] || [],
    };
};

// Map dispatch to props
const mapDispatchToProps = {
    updateProbedQuestion,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProbedQuestionsContainer);
